import { ReactNode, useState } from 'react';
import { lt } from '@mews/framework';
import { CompoundField, CompoundFieldProps } from '@optimus-web/core';
import { NumberInput } from '../NumberInput';

type DurationUnitName = 'months' | 'days' | 'hours' | 'minutes' | 'seconds';
type DurationUnitMap<T> = { [key in DurationUnitName]?: T };

export type DurationObject = DurationUnitMap<number>;

export interface DurationInputProps extends Omit<CompoundFieldProps, 'children' | 'error'> {
    autoFocus?: boolean;
    defaultValue?: DurationObject;
    units: DurationUnitMap<boolean>;
    onChange?: (value: DurationObject) => void;
    error?: DurationUnitMap<ReactNode>;
}

export const DurationInput = ({ onChange, units, error, helper, defaultValue = {}, ...props }: DurationInputProps) => {
    const [duration, setDuration] = useState<DurationObject>(defaultValue);
    const handleChange = (value: number | undefined, unit: DurationUnitName) => {
        const nextDuration = {
            ...duration,
            [unit]: value,
        };
        setDuration(nextDuration);
        onChange?.(nextDuration);
    };

    return (
        <CompoundField {...props} helper={error ? null : helper}>
            {units.months ? (
                <NumberInput
                    name="months"
                    suffix={lt('Months')}
                    value={duration.months}
                    onChange={(value) => handleChange(value, 'months')}
                    error={error?.months}
                />
            ) : null}
            {units.days ? (
                <NumberInput
                    name="days"
                    suffix={lt('Days')}
                    value={duration.days}
                    onChange={(value) => handleChange(value, 'days')}
                    error={error?.days}
                />
            ) : null}
            {units.hours ? (
                <NumberInput
                    name="hours"
                    suffix={lt('Hours')}
                    value={duration.hours}
                    onChange={(value) => handleChange(value, 'hours')}
                    error={error?.hours}
                />
            ) : null}
            {units.minutes ? (
                <NumberInput
                    name="minutes"
                    suffix={lt('Minutes')}
                    value={duration.minutes}
                    onChange={(value) => handleChange(value, 'minutes')}
                    error={error?.minutes}
                />
            ) : null}
            {units.seconds ? (
                <NumberInput
                    name="seconds"
                    suffix={lt('Seconds')}
                    value={duration.seconds}
                    onChange={(value) => handleChange(value, 'seconds')}
                    error={error?.seconds}
                />
            ) : null}
        </CompoundField>
    );
};
